import ReactDOM from "react-dom";
import axios from "axios";
import deepmerge from "deepmerge";

import WRAP from "WRAP-JS";
import CustomTab from "../../../parts/CustomTab";
import CustomTable from "../../../parts/CustomTable";
import { getCustomerSettings } from "../../../CustomerSettings";
import * as FPEUtils from "../../../utils/FPEUtils";
import { getCycleDate } from "../../../Authorization";
import { configFlightPlanListDh } from "./config/ConfigFlightPlan";
import { MAP_ID_FLIGHTPLAN_LIST } from "../../../store/MapContentsState";
import { isEqualFlightPlan, searchFlightPlan } from "../../../store/FlightPlanState";
import { executeLinksAPI } from "../../../utils/LinksAPI";
import { ReactComponent as ImageSquare } from '../../../images/point/default_icon/square.svg';
import { ReactComponent as ImageSpot } from '../../../images/point/default_icon/spot.svg';
import { ReactComponent as ImageHospital } from '../../../images/point/default_icon/hospital.svg';
import { ReactComponent as ImageHeliport } from '../../../images/point/default_icon/heliport.svg';
import { ReactComponent as ImageSubstation } from '../../../images/point/default_icon/substation.svg';
import { IconButton, SvgIcon, TableCell, Tooltip } from "@material-ui/core";
import VisibilityIcon from '@material-ui/icons/Visibility';
import { useState } from "react";
import { useCallback } from "react";
import { COMMUNICATE_ERROR_MESSAGE_GET_FLIGHT_PLAN, COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER } from "../../../constants/ErrorMessage";

class FlightPlanList {
  static NAME = MAP_ID_FLIGHTPLAN_LIST;

  usergroupId;
  dispatcher;
  contentsDispatcher;
  mapDiv;
  popupBaseDiv;
  dh;
  layer;
  labelLayer;
  arrowLayer;
  flightPlanList;
  flightPlanFixList;
  flightPlanLabelList;
  detailTarget;
  editMode;
  visible;
  ready = false;
  eventListeners = [];
  autoUpdate = true;
  editing = false;
  lockEvent = false;
  originalPoint = null;
  currentPopup = null;
  overlay = null;
  overlayListenerFunc = null;
  planUpdateCallbackFunc = null;
  highlightPointsCallbackFunc = null;
  highlightPlanCallbackFunc = null;
  errorFunc = null;
  mouseover;
  mousePosition;
  scrollTimer = 0;
  highlightFeatures = [];
  depArrFeatures = [];
  depArrLabelFeatures = [];
  isDark;
  draggingPoint;

  cycleDate = getCycleDate();
  dispatcher;
  baseTime = '---';
  errorMessageInfo;

  constructor(isDark, usergroupId) {
    this.isDark = isDark;
    this.usergroupId = usergroupId;
    this.layer = new WRAP.Geo.Layer(FlightPlanList.NAME);
    this.labelLayer = new WRAP.Geo.Layer(`${FlightPlanList.NAME}_label`);
    this.arrowLayer = new WRAP.Geo.Layer(`${FlightPlanList.NAME}_arrow`);
    this.layer.setVisible(false);
    this.labelLayer.setVisible(false);
    this.arrowLayer.setVisible(false);
    this.arrowLayer.disableInteraction(true);
  }

  setDispatcher(dispatcher) {
    this.dispatcher = dispatcher;
  }

  setContentsDispatcher(dispatcher) {
    this.contentsDispatcher = dispatcher;
  }

  setFlightPlanList(list) {
    this.flightPlanList = deepmerge([], list.filter(fp => fp.visible));
    this._makeFixes();
    this._redraw();
  }

  setTarget(target, editMode) {
    this.detailTarget = target && target !== '' ? deepmerge({}, target) : target;
    this.editMode = editMode;

    if (!this.ready) return;

    if (!this.editMode) {
      this._clearDepArrFeature(false);
    }

    //自動更新設定
    const beforeEnabled = this.visible && this.autoUpdate;
    this.autoUpdate = !this.editMode;
    const enabled = this.visible && this.autoUpdate;
    if (beforeEnabled !== enabled) {
      this.dh.setAutoUpdate('data', enabled);
    }

    this._makeFixes();
    this._redraw();
  }

  reloadPlan() {
    if (!this.ready) return;
    this.dh.setAutoUpdate('data', this.autoUpdate);
  }

  async initialize() {
    await this.initData();
    this.initLayer();
    this.ready = true;
  }

  initData() {
    return new Promise(resolve => {
      this.dh = new WRAP.Data(configFlightPlanListDh);
      this.dh.set('data', {
        usergroup_id: this.usergroupId
      });
      this.dh.updated('data', ref => {
        if (this.errorMessageInfo) {
          this.contentsDispatcher({type: 'CLEAR_ERROR', payload: this.errorMessageInfo});
          this.errorMessageInfo = null;
        }
        this._parseData(ref);
        this.setPlanDataRevision();
        resolve(ref);
      }, () => {
        this.errorMessageInfo = this.contentsDispatcher({type: 'SHOW_ERROR', payload: {
          message: COMMUNICATE_ERROR_MESSAGE_GET_FLIGHT_PLAN,
          additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
        }});
        if (!this.ready) {
          this.dispatcher({type: 'DATA_LOAD_ERROR'});
        }
      });
    });
  }

  initLayer() {
    this.mapDiv = document.getElementById('map-container');
    this.popupBaseDiv = document.getElementById('app');

    this.addEventListeners();

    WRAP.Geo.addEventHandler('mouseover', (layer, feature, sp) => {
      if(layer === this.layer) {
        if(feature instanceof WRAP.Geo.Feature.GeoLine) {
          const targetFlightPlan = this._isDetailTarget(feature) ? this.detailTarget : searchFlightPlan(this.flightPlanList, feature.props);
          if (targetFlightPlan) {
            const currentRoute = targetFlightPlan.routeList[feature.index];
            const beforeRoute = targetFlightPlan.routeList[feature.index - 1];
            if(['fix', 'gps', 'gmap', 'plan', 'arr', 'landmark', 'airport', 'heliport'].includes(currentRoute.type)) {
              this.dispatchHighlightPoints([{
                source: 'horizontal',
                type: 'route',
                name: beforeRoute.name,
                lon: beforeRoute.coordinate[0],
                lat: beforeRoute.coordinate[1],
                index: feature.index - 1
              }, {
                source: 'horizontal',
                type: 'route',
                name: currentRoute.name,
                lon: currentRoute.coordinate[0],
                lat: currentRoute.coordinate[1],
                index: feature.index
              }]);
            } else if(currentRoute.type === 'airway') {
              this.dispatchHighlightPoints(
                  currentRoute.fixes.map(fix => {
                    return {
                      source: 'horizontal',
                      type: 'airway',
                      name: fix.name,
                      lon: fix.coordinate[0],
                      lat: fix.coordinate[1],
                      index: feature.index
                    };
                  })
              );
            }
          }
        } else if(feature instanceof WRAP.Geo.Feature.Point) {
          if(feature.data) {
            const data = feature.data;
            this.dispatchHighlightPoints([{
              source: 'horizontal',
              type: 'fix',
              name: data.name,
              lon: data.coordinate[0],
              lat: data.coordinate[1],
              index: feature.index
            }]);
          }
        }

        if(!this.editMode) {
          if(feature instanceof WRAP.Geo.Feature.GeoLine || feature instanceof WRAP.Geo.Feature.Point) {
            this.mouseover = feature;
            this.mapDiv.style.cursor = "pointer";

            const targetFlightPlan = searchFlightPlan(this.flightPlanList, feature.props);
            if (targetFlightPlan) {
              this.dispatchHighlightPlan(targetFlightPlan);
            }
          }
          return;
        }
        if(!this._isDetailTarget(feature)) {
          return;
        }
        if(feature instanceof WRAP.Geo.Feature.GeoLine) {
          this.mouseover = feature;
          this.mapDiv.style.cursor = "pointer";
        } else if(feature instanceof WRAP.Geo.Feature.Point) {
          this.mouseover = feature;
          if(feature.editable) {
            this.mapDiv.style.cursor = "move";
          } else if(feature.permeate) {
            this.mapDiv.style.cursor = "pointer";
          }
          //DEP, ARRのみ(経路の線が見えない)ルートの場合
          else if (this.detailTarget && this.detailTarget.routeList.length === 2
            && this.detailTarget.routeList[0].coordinate[0] === this.detailTarget.routeList[1].coordinate[0]
            && this.detailTarget.routeList[0].coordinate[1] === this.detailTarget.routeList[1].coordinate[1]) {
            this.mapDiv.style.cursor = "pointer";
          }
        }
      } else if (layer === this.labelLayer) {
        if(!this.editMode) {
          return;
        }
        if(!this._isDetailTarget(feature)) {
          return;
        }
        if(feature instanceof WRAP.Geo.Feature.Text) {
          //ラベルに合致するPoint featureを特定
          const features = this.layer.getDisplayList();
          for(const f of features) {
            if (!(f instanceof WRAP.Geo.Feature.Point)) {
              continue;
            }
            if (!isEqualFlightPlan(f.props, feature.props)) {
              continue;
            }
            //同プランかつindexが同じfeatureを対象とする。ただしpermeateがtrue(=Airway上の点なので対象外)
            if (f.index === feature.index && !f.permeate) {
              this.mouseover = f;
              break;
            }
          }
          if (!this.mouseover) {
            return;
          }
          if (this.mouseover.editable) {
            this.mapDiv.style.cursor = "move";
          }
        }
      }
    });

    WRAP.Geo.addEventHandler('mouseout', (layer, feature, sp) => {
      if(layer === this.layer) {
        this.dispatchHighlightPoints([]);
        if(!this.editMode) {
          if(feature instanceof WRAP.Geo.Feature.GeoLine || feature instanceof WRAP.Geo.Feature.Point) {
            this.mouseover = null;
            this.mapDiv.style.cursor = "auto";
          }
          this.dispatchHighlightPlan();
          return;
        }
        if(!this._isDetailTarget(feature)) {
          return;
        }
        this.mouseover = null;
        if(feature instanceof WRAP.Geo.Feature.GeoLine) {
          this.mapDiv.style.cursor = "auto";
        } else if(feature instanceof WRAP.Geo.Feature.Point) {
          this.mapDiv.style.cursor = "auto";
        }
      } else if (layer === this.labelLayer) {
        if(!this.editMode) {
          return;
        }
        this.mouseover = null;
        if(feature instanceof WRAP.Geo.Feature.Text) {
          this.mapDiv.style.cursor = "auto";
        }
      }
    });

    WRAP.Geo.addEventHandler('boundsChange', () => {
      this._updateBounds();
    });
  }

  async drawLayer(visible) {
    if (!this.ready) {
      await this.initialize();
    }
    this.setVisible(visible);
  }

  getLayer() {
    return [this.labelLayer, this.layer, this.arrowLayer];
  }

  setVisible(visible) {
    const beforeEnabled = this.visible && this.autoUpdate;
    this.visible = visible;

    if (!this.ready) return;

    //自動更新設定
    const enabled = this.visible && this.autoUpdate;
    if (beforeEnabled !== enabled) {
      this.dh.setAutoUpdate('data', enabled);
    }

    if (visible) {
      this.addEventListeners();
      this.layer.show();
      this.labelLayer.show();
      this.arrowLayer.show();
    } else {
      this.removeEventListeners();
      this.layer.hide();
      this.labelLayer.hide();
      this.arrowLayer.hide();
    }
  }

  addEventListeners() {
    if (this.mapDiv && this.eventListeners.length === 0) {
      this.eventListeners.push({
        type: 'mousedown',
        listener: (event) => {
          if(this.lockEvent) {
            return;
          }
          // 右クリックは無視(地点情報表示と被るため)
          if (event.button === 2) {
            return;
          }
          if(!this.mouseover) {
            const depArrSelected = this._isDepArrSelected();
            if (depArrSelected && !depArrSelected.selected) {
              const rect = this.mapDiv.getBoundingClientRect();
              const mouseDownX = event.clientX - rect.left;
              const mouseDownY = event.clientY - rect.top;

              let mouseupHandle;
              window.addEventListener('mouseup', mouseupHandle = ev => {
                window.removeEventListener('mouseup', mouseupHandle);
                const mouseUpX = ev.clientX - rect.left;
                const mouseUpY = ev.clientY - rect.top;
                const dist = Math.sqrt( Math.pow( mouseUpX-mouseDownX, 2 ) + Math.pow( mouseUpY-mouseDownY, 2 ) );
                //mousedown時とmouseup時のスクリーン座標が10ピクセル(調整値)以上離れていたらクリックではなくドラッグとみなして何もしない
                if (dist > 10) {
                  return;
                }

                let coordinate;
                let mode;
                //DEP
                if (!depArrSelected.depSelected) {
                  mode = 'dep';
                  coordinate = this.depArrFeatures[0].style().point;
                }
                //ARR
                else {
                  /**
                   * Map上でDepを決定し、mouseを動かさずにクリックした場合はクリックした位置をArrにする
                   */
                  if (!this.depArrFeatures[1]) {
                    this._redrawDepArrFeature({x: mouseDownX, y: mouseDownY});
                  }
                  mode = 'arr';
                  coordinate = this.depArrFeatures[1].style().point;
                }
                this._searchNearPoint(coordinate, mode);

              }, false);
            }
            return;
          }
          if(!this.editMode) {
            this.lockEvent = true;
            const rect = this.mapDiv.getBoundingClientRect();
            const x = event.clientX - rect.left;
            const y = event.clientY - rect.top;
            const radius = 30; // pixel
            let list = WRAP.Geo.getFeatures({ point: [x, y], radius: radius});
            list = list.filter(feature => {
              const d = feature.data;
              return d && d.props && d.props.customerCode;
            });
            if (!list || list.length === 0) {
              list = [this.mouseover];
            }

            const planList = Array.from(
              new Map(list.map((feature) => {
                const p = feature.props || feature.data.props;
                return [`${p.flightplan_id}-${p.customerCode}-${p.flightNo}`, p];
              })).values()
            ).sort((a,b) => {
              if (a.etd === b.etd) {
                return a.aircraftCode < b.aircraftCode ? -1 : 1;
              } else {
                return a.etd < b.etd ? -1 : 1;
              }
            });

            const overlay = document.createElement('div');
            overlay.id = 'overlay';
            overlay.style.position = 'absolute';
            overlay.style.left = 0;
            overlay.style.top = 0;
            overlay.style.width = '100%';
            overlay.style.height = '100%';
            overlay.style.backgroundColor = 'rgba(128, 128, 128, 0.5)';
            overlay.style.zIndex = 1000;
            this.overlayListenerFunc = () => {
              overlay.removeEventListener('click', this.overlayListenerFunc);
              overlay.remove();
              if(this.currentPopup !== null) {
                this.popupBaseDiv.removeChild(this.currentPopup);
                this.currentPopup = null;
              }
              this.mouseover = null;
              this.lockEvent = false;
            }
            this.popupBaseDiv.appendChild(overlay);

            const OFFSET_X = 50;
            const OFFSET_Y = 6;
            this.currentPopup = this._createPlanListPopup(planList);
            const pw = this.mapDiv.clientWidth;
            const ph = this.mapDiv.clientHeight;
            const bottomDiff = this.popupBaseDiv.clientHeight - ph;
            const rightDiff = this.popupBaseDiv.clientWidth - pw;
            const sp = {x: event.clientX, y: event.clientY};
            Object.assign(this.currentPopup.style, {
              visibility: 'hidden'
            });
            this.popupBaseDiv.appendChild(this.currentPopup);

            let left, right, top, bottom;
            if(pw / 2 > sp.x) {
              left = sp.x + OFFSET_X;
              if (pw - left < this.currentPopup.offsetWidth) {
                left = pw - this.currentPopup.offsetWidth;
              }
            } else {
              right = pw - sp.x + OFFSET_X + rightDiff;
              if (pw - right < this.currentPopup.offsetWidth) {
                right = pw - this.currentPopup.offsetWidth;
              }
            }
            if(ph / 2 > sp.y) {
              top = sp.y + OFFSET_Y;
            } else {
              bottom = ph - sp.y + OFFSET_Y + bottomDiff;
            }
            Object.assign(this.currentPopup.style, {
              left: left !== undefined ? `${left}px` : 'auto',
              right: right !== undefined ? `${right}px` : 'auto',
              top: top !== undefined ? `${top}px` : 'auto',
              bottom: bottom !== undefined ? `${bottom}px` : 'auto',
              visibility: 'visible'
            });
            overlay.addEventListener('click', this.overlayListenerFunc);

          } else if (!this.draggingPoint) {
            this._handleDrag(event.clientX, event.clientY);
            event.stopPropagation();
          }
        }
      });
      this.eventListeners.push({
        type: 'mousemove',
        listener: (event) => {
          this._redrawDepArrFeature({x: event.clientX, y: event.clientY});
        }
      });

      for (const el of this.eventListeners) {
        this.mapDiv.addEventListener(el.type, el.listener, true);
      }
    }
  }

  removeEventListeners() {
    if (this.mapDiv && this.eventListeners.length !== 0) {
      let el;
      while( (el = this.eventListeners.shift()) !== undefined ) {
        this.mapDiv.removeEventListener(el.type, el.listener, true);
      }
    }
  }

  onPlanUpdate(func) {
    this.planUpdateCallbackFunc = func;
  }

  onHighlightPoints(func) {
    this.highlightPointsCallbackFunc = func;
  }

  dispatchHighlightPoints(points, fource = false) {
    if (this.currentPopup === null || fource) {
      if(typeof this.highlightPointsCallbackFunc === "function") {
        this.highlightPointsCallbackFunc(points);
      }
    }
  }

  onHighlightPlan(func) {
    this.highlightPlanCallbackFunc = func;
  }

  dispatchHighlightPlan(plan, fource = false) {
    if (this.currentPopup === null || fource) {
      if(typeof this.highlightPlanCallbackFunc === "function") {
        this.highlightPlanCallbackFunc({
          source: 'horizontal',
          plan: plan,
        });
      }
    }
  }

  onError(func) {
    this.errorFunc = func;
  }

  highlightPlan(data) {
    if (data.source === 'horizontal') {
      return;
    }

    //ハイライト表示を削除
    if (this.highlightFeatures.length > 0) {
      let f;
      while( (f = this.highlightFeatures.shift()) !== undefined ) {
        this.layer.removeFeature(f);
      }
    }

    const highlightTarget = data.plan;
    if (highlightTarget && highlightTarget.routeList) {
      //選択されたプランをハイライト
      const lineFeatures = [];
      const pointFeatures = [];
      const airwayPointFeatures = [];
      const highlightStyle = {
        line: { strokeStyle: 'orange', lineWidth: 7 },
        point: { fillStyle: 'orange' },
      };
      this._makeFeatures(highlightTarget, lineFeatures, pointFeatures, airwayPointFeatures, highlightStyle, true);

      this.highlightFeatures.push(...lineFeatures);
      this.highlightFeatures.push(...airwayPointFeatures);
      this.highlightFeatures.push(...pointFeatures);
      this.highlightFeatures.forEach(f => this.layer.addFeature(f));
    }
    // WebGLの再描画条件
    this.layer._immediate = true;
    WRAP.Geo.invalidate();
  }

  highlightPoints(highlightPoints) {
    //ハイライト表示を削除
    if (this.highlightFeatures.length > 0) {
      let f;
      while( (f = this.highlightFeatures.shift()) !== undefined ) {
        this.layer.removeFeature(f);
      }
    }

    if (highlightPoints && highlightPoints.length > 0) {
      if(highlightPoints.length === 1) {
        const point = highlightPoints[0];
        if(point.source !== 'horizontal' && point.lat !== null && point.lon !== null) {
          switch(point.type) {
            case 'fix':
              this.highlightFeatures.push(new WRAP.Geo.Feature.Point({
                point: [point.lon, point.lat],
                strokeStyle:'#880',
                lineWidth: 2,
                fillStyle: '#ff7b00',
                pointSize: 12
              }));
              break;
            default:
              break;
          }
        }
      } else {
        if(highlightPoints[0].source !== 'horizontal') {
          this.highlightFeatures.push(new WRAP.Geo.Feature.GeoLine({
            path: highlightPoints.filter(p => p.lon !== null && p.lat !== null).map(p => [p.lon, p.lat]),
            strokeStyle: "rgba(255, 123, 0, 0.5)",
            lineWidth: 7,
            method: "GC"
          }));
        }
      }
      this.highlightFeatures.forEach(f => this.layer.addFeature(f));
    }
    // WebGLの再描画条件
    this.layer._immediate = true;
    WRAP.Geo.invalidate();
  }

  setDraggingPoint(draggingPoint) {
    if(!this.editMode) {
      return;
    }

    this.draggingPoint = draggingPoint;
    if (!this.draggingPoint) {
      this.mouseover = null;
      return;
    }

    //指定されたPoint featureを特定
    const features = this.layer.getDisplayList();
    for(const f of features) {
      if (!draggingPoint.data.temporary) {
        if (!(f instanceof WRAP.Geo.Feature.Point)) {
          continue;
        }
      }
      if (draggingPoint.data.temporary) {
        if (!(f instanceof WRAP.Geo.Feature.GeoLine)) {
          continue;
        }
      }

      //同プランでない場合はスキップ
      if (!this._isDetailTarget(f)) {
        continue;
      }
      //indexが同じfeatureを対象とする。ただしPointの場合はpermeateがtrue(=Airway上の点なので対象外)
      if (draggingPoint.data.temporary) {
        if (f.index === draggingPoint.data.index + 1) {
          this.mouseover = f;
          break;
        }
      } else {
        if (f.index === draggingPoint.data.index && !f.permeate) {
          this.mouseover = f;
          break;
        }
      }
    }
    if (!this.mouseover) {
      return;
    }
    this._handleDrag(draggingPoint.pos.x, draggingPoint.pos.y);
  }

  applyMapColor(mapColor, isDark) {
    this.isDark = isDark;
    this._redrawLabel();
    // this._redrawArrow();
    this.depArrLabelFeatures.forEach(f => {
      f.style().fillStyle = this._labelFillStyle();
    });
    WRAP.Geo.invalidate();
  }

  _parseData(response) {
    const flightPlanList = [];
    Object.keys(response).forEach(day => {
      response[day].forEach(data => {
        //データ不備等でDEP,ARRの座標情報が無いデータはスキップ
        if (!data.dep_point_lat || !data.dep_point_lon || !data.arr_point_lat || !data.arr_point_lon) {
          return;
        }
        const flightBaseInfo = {
          flightPlanId: data.flightplan_id,
          flightNo: data.flight_no,
          flightPlanName: data.flightplan_name,
          customerCode: data.customer_code,
          flightPlanUpdateTime: data.flightplan_update_time,
          aircraftCode: data.aircraft_code,
          flightType: data.flight_type,
          skd: data.skd,
          etd: data.etd_utc,
          eta: data.eta_utc,
          sta: data.sta_utc,
          depPointName: data.dep_point_name,
          arrPointName: data.arr_point_name,
        };

        const flightInfo = {
          ...flightBaseInfo,
          atd: data.atd_utc,
          ata: data.ata_utc,
          depAirportIcao: data.dep_airport_icao,
          depAirportIata: data.dep_airport_iata,
          arrAirportIcao: data.arr_airport_icao,
          arrAirportIata: data.arr_airport_iata,
          etdLt: data.etd_lt,
          staLt: data.sta_lt,
          etaLt: data.eta_lt,
          atdLt: data.atd_lt,
          ataLt: data.ata_lt,
          scheduledRoute: data.scheduled_route,
          isLinksPlan: data.is_links_plan,
          hasUnknownPoint: !data.is_links_plan && data.has_unknown_point,  //Links作成プランの場合は赤表示にしない
        };

        const routeList = [];
        this._convertPoint(routeList, "dep", {
          name: data.dep_point_name,
          lat: data.dep_point_lat,
          lon: data.dep_point_lon,
          altitude_ft: data.dep_point_alt_ft
        }, flightBaseInfo);

        if (data.waypoint) {
          data.waypoint.forEach((wp) => {
            this._convertPoint(routeList, wp.type, wp, flightBaseInfo);
          });
        }

        this._convertPoint(routeList, "arr", {
          name: data.arr_point_name,
          lat: data.arr_point_lat,
          lon: data.arr_point_lon,
          altitude_ft: data.arr_point_alt_ft
        }, flightBaseInfo);

        //地点間の距離を算出してセット
        let prevPoint;
        routeList.forEach((p, i, pList) => {
          if (i === 0) {
            p.dist = 0;
            prevPoint = p;
          } else {
            if (p.type === 'airway') {
              p.fixes.forEach((ap, j, apList) => {
                if (j === 0) {
                  ap.dist = prevPoint.dist;
                } else if (j < apList.length - 1) {
                  ap.dist = this._getDistance(prevPoint, ap);
                  prevPoint = ap;
                }
              });
            } else {
              p.dist = this._getDistance(prevPoint, p);
              if (pList[i - 1].type === 'airway') {
                const fixes = pList[i - 1].fixes;
                fixes[fixes.length -1].dist = p.dist;
              }
              prevPoint = p;
            }
          }
        });
        flightPlanList.push({...flightInfo, routeList});
      });
    });

    flightPlanList.sort((a,b) => {
      if (a.etd === b.etd) {
        return a.aircraftCode < b.aircraftCode ? -1 : 1;
      } else {
        return a.etd < b.etd ? -1 : 1;
      }
    });

    this.dispatcher({type: 'SET_FLIGHT_PLAN_LIST', payload: flightPlanList});
  }

  _convertPoint(routeList, type, data, flightInfo) {
    //緯度経度が入っていない地点はスキップ
    if (data.lon === null || data.lat === null) {
      return;
    }
    const _data = this._makePoint(type, data, flightInfo);
    let dt;
    if (type === "dep") {
      dt = new WRAP.Core.DateTime(flightInfo.etd);
    } else if (type === "arr") {
      dt = new WRAP.Core.DateTime(flightInfo.eta ? flightInfo.eta : flightInfo.sta);
    } else {
      if (typeof data.time === "number") {
        dt = new WRAP.Core.DateTime(new Date(data.time));
      } else if (data.time) {
        dt = new WRAP.Core.DateTime(data.time);
      }
    }
    _data.transitTime = dt ? dt.date.getTime() : undefined;

    if (type === "airway-fix") {
      _data.minAlt = data.min_altitude_ft;
      _data.maxAlt = data.max_altitude_ft;
    }

    //最初の地点(=dep)
    if (routeList.length === 0) {
      routeList.push(_data);
      return;
    }

    const before = routeList[routeList.length - 1];
    //Airwayの場合
    if (type === "airway-fix") {
      const airwayName = data.airway_name;

      //1つ前が同一Airwayであれば地点を追加
      if (before.type === "airway" && before.name === airwayName) {
        before.fixes.push(_data);
        return;
      }
      //1つ前がAirwayでない or 別のAirwayの場合
      else {
        //1つ前の地点がAirwayの先頭と異なる地点の場合はfix要素を間に挿入
        if (before.name !== _data.name || before.coordinate[0] !== _data.coordinate[0] || before.coordinate[1] !== _data.coordinate[1]
          || before.alt !== _data.alt || before.transitTime !== _data.transitTime) {
          const _fixData = this._makePoint("fix", data, flightInfo);
          _fixData.transitTime = _data.transitTime;
          routeList.push(_fixData);
        }
        routeList.push({
          type: "airway",
          name: airwayName,
          fixes: [_data],
          props: flightInfo,
          refs: FPEUtils.deepCopy(data),
        });
        return;
      }
    }
    //Airwayでない場合
    else {
      //1つ前がAirwayの場合
      if (before.type === "airway") {
        //1つ前のAirwayの最後と異なる地点の場合はfix要素を間に挿入
        const _lastAirwayFix = before.fixes[before.fixes.length - 1];
        if (_lastAirwayFix.name !== _data.name || _lastAirwayFix.coordinate[0] !== _data.coordinate[0] || _lastAirwayFix.coordinate[1] !== _data.coordinate[1]
          || _lastAirwayFix.alt !== _data.alt || _lastAirwayFix.transitTime !== _data.transitTime) {
          const _fixData = {..._lastAirwayFix};
          _fixData.type = "fix";
          delete _fixData.minAlt;
          delete _fixData.maxAlt;
          routeList.push(_fixData);
        }
      }

      routeList.push(_data);
    }
  }

  _makePoint(type, data, flightInfo) {
    return {
      type: type,
      name: data.name,
      coordinate: [data.lon-0, data.lat-0],
      alt: data.altitude_ft ? data.altitude_ft-0 : 0,
      props: flightInfo,
      refs: FPEUtils.deepCopy(data),
    };
  }

  _isDetailTarget(feature) {
    if (!this.detailTarget) {
      return false;
    }
    if (!feature || !feature.props) {
      return false;
    }
    return isEqualFlightPlan(this.detailTarget, feature.props)
  }

  _updateBounds() {
    this._redrawLabel();
    this._redrawArrow();
    WRAP.Geo.invalidate();
  }

  _makeFixes() {
    const flightPlanFixList = [];
    if (this.detailTarget) {
      this._makeRouteFixes(this.detailTarget, flightPlanFixList);
    }
    this.flightPlanList.forEach(plan => {
      if (isEqualFlightPlan(this.detailTarget, plan)) {
        return;
      }
      this._makeRouteFixes(plan, flightPlanFixList);
    });
    this.flightPlanFixList = flightPlanFixList;
  }

  _makeRouteFixes(plan, flightPlanFixList) {
    //name,緯度経度が2点(dep,arr)揃っていないと対象としない
    if (!plan || !plan.routeList || plan.routeList.length < 2
      || !plan.routeList[0].name || plan.routeList[0].coordinate.filter(v => v !== null).length !== 2
      || !plan.routeList[plan.routeList.length-1].name || plan.routeList[plan.routeList.length-1].coordinate.filter(v => v !== null).length !== 2) {
      return;
    }

    const routeFixList = [];
    let count = 0;
    plan.routeList.forEach(route => {
      if(route.type === 'airway') {
        for(let i = 1; i < route.fixes.length - 1; i++) {
          const fix = route.fixes[i];
          fix.fixindex = count++;
          fix.type = 'airway-fix';
          routeFixList.push(fix);
        }
      } else {
        route.fixindex = count++;
        routeFixList.push(route);
      }
    });
    flightPlanFixList.push(routeFixList);
    return;
  }

  _createPlanListPopup(planList) {
    let previewFeatures = [];
    const removePreviewFeatures = () => {
      let previewFeature;
      while( (previewFeature = previewFeatures.shift()) !== undefined ) {
        this.layer.removeFeature(previewFeature);
      }
    };
    const onMouseEnter = plan => {
      const target = searchFlightPlan(this.flightPlanList, plan);
      if(target === null) {
        return;
      }
      //選択されたプランをハイライト
      const lineFeatures = [];
      const pointFeatures = [];
      const airwayPointFeatures = [];
      const previewStyle = {
        line: { strokeStyle: 'orange' },
        point: { fillStyle: 'orange' },
      };
      this._makeFeatures(target, lineFeatures, pointFeatures, airwayPointFeatures, previewStyle, true);

      removePreviewFeatures();
      previewFeatures.push(...lineFeatures);
      previewFeatures.push(...airwayPointFeatures);
      previewFeatures.push(...pointFeatures);
      previewFeatures.forEach(f => this.layer.addFeature(f));
      // WebGLの再描画条件
      this.layer._immediate = true;
      WRAP.Geo.invalidate();

      //FlightPlanListView側もハイライトさせる
      this.dispatchHighlightPlan(target, true);
    };
    const onMouseLeave = () => {
      //ハイライト表示を削除
      removePreviewFeatures();
      // WebGLの再描画条件
      this.layer._immediate = true;
      WRAP.Geo.invalidate();

      //FlightPlanListView側のハイライト解除
      this.dispatchHighlightPlan(undefined, true);
    };
    const onClick = plan => {
      const target = searchFlightPlan(this.flightPlanList, plan);
      if(target !== null) {
        onMouseLeave();
        this.dispatchHighlightPoints([], true);
        this.dispatcher({type: 'START_EDIT', payload: target});
      }
      this.overlayListenerFunc();
    };
    const onClickVisibility = (event, target) => {
      if(target !== null) {
        onMouseLeave();
        this.dispatchHighlightPoints([], true);
        this.dispatcher({type: 'HIDE_PLAN', payload: target});
      }
      this.overlayListenerFunc();
      event.stopPropagation();
    };
    const formatDate = dateStr => {
      return FPEUtils.formatHHMM(dateStr);
    };
    const formatNullable = value => {
      return value !== null && value !== undefined ? value : '---';
    };
    const renderHideCell = (plan, column) => {
      const target = searchFlightPlan(this.flightPlanList, plan);
      if(target === null || !target.isLinksPlan) {
        return (
          <TableCell key={column.id} align={column.align} style={{padding: "0"}}></TableCell>
        );
      }
      return (
        <TableCell key={column.id} align={column.align} style={{padding: "0"}}>
          <HideButton target={target} onClick={onClickVisibility} />
        </TableCell>
      );
    };
    const overflowStyle = {
      maxWidth: 150,
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis'
    };
    const columns = [
      { id: "aircraftCode", label: "Aircraft Code", key: "aircraftCode", formatter: formatNullable },
      { id: "flightType", label: "Flight Type", key: "flightType", formatter: formatNullable },
      { id: "dep", label: "DEP", key: "depPointName", style: overflowStyle, title: true },
      { id: "arr", label: "ARR", key: "arrPointName", style: overflowStyle, title: true },
      { id: "etd", label: `ETD(${FPEUtils.formatTimeZoneStr()})`, key: "etd", formatter: formatDate },
      { id: "hideButton", label: "", key: "hideButton", render: renderHideCell },
    ];
    const classes = {
      table: {
        "& .MuiTableRow-root.MuiTableRow-hover:hover": {
          backgroundColor: "orange",
        },
        "& .MuiTableCell-sizeSmall": {
          lineHeight: "1rem",
          padding: "4px 18px 4px 12px",
        },
      }
    };
    const popup = document.createElement('div');
    popup.id = 'popup';
    ReactDOM.render(
      <>
        <div className="title">{FPEUtils.translation(planList.length > 1 ? 'Flight plan list' : 'Flight plan')}</div>
        <CustomTable aria-label="flightplan list table"
          size="small" classes={classes}
          onMouseLeave={onMouseLeave} onMouseOver={onMouseEnter} onClick={onClick}
          columns={columns} rows={planList} />
      </>
    , popup);
    return popup;
  }

  _nextFix(index, direction) {
    let i = index;
    let delta;
    if(direction > 0 || direction === true) {
      delta = 1;
    } else {
      delta = -1;
    }

    while(true) {
      i += delta;
      if(delta === 1 && i >= this.detailTarget.routeList.length) {
        return null;
      }
      if(delta === -1 && i < 0) {
        return null;
      }

      if(['dep', 'arr', 'fix', 'gps', 'gmap', 'plan', 'landmark', 'airport', 'heliport'].includes(this.detailTarget.routeList[i].type)) {
        return this.detailTarget.routeList[i];
      }
    }
  }

  _handleDrag(clientX, clientY) {
    let dragFlg;
    const set = (clientX, clientY, point) => {
      const rect = this.mapDiv.getBoundingClientRect();
      let x = clientX - rect.left;
      let y = clientY - rect.top;
      if(x < 0) {
        x = 0;
      } else if(x > rect.width) {
        x = rect.width;
      }
      if(y < 0) {
        y = 0;
      } else if(y > rect.height) {
        y = rect.height;
      }
      const sp = {x, y};
      const p = WRAP.Geo.getPoint(sp);
      point[0] = p.lonDegree();
      point[1] = p.latDegree();
      // WebGLの再描画条件
      this.layer._modified = true;
      this.layer._immediate = true;
      WRAP.Geo.invalidate();
    }
    const mousemove = event => {
      if(this.lockEvent) {
        return;
      }
      const x = event.clientX;
      const y = event.clientY;
      this.mousePosition = {x, y};
      set(event.clientX, event.clientY, point);
    }
    this.mapDiv.classList.add('drag');

    const view = WRAP.Geo.currentView();
    view.enableScroll(false);
    let feature = undefined;
    let style = undefined;
    let point = undefined;

    //DEP, ARRのみ(経路の線が見えない)ルートの場合
    if (!this.draggingPoint && this.detailTarget && this.detailTarget.routeList.length === 2
      && this.detailTarget.routeList[0].coordinate[0] === this.detailTarget.routeList[1].coordinate[0]
      && this.detailTarget.routeList[0].coordinate[1] === this.detailTarget.routeList[1].coordinate[1]) {

      //該当便のDEP-ARRを結ぶGeoLineを検索
      const features = this.layer.getDisplayList().filter(f => {
        return f instanceof WRAP.Geo.Feature.GeoLine && this._isDetailTarget(f);
      });
      if (features.length === 0) {
        return;
      }

      feature = features[0];
      style = feature.style();
      point = [];
      style.path.splice(1, style.path.length - 2, point);

    } else if (this.mouseover.editable || this.mouseover.permeate) {

      feature = this.mouseover;
      style = feature.style();

      if(feature instanceof WRAP.Geo.Feature.GeoLine) {
        point = [];
        style.path.splice(1, style.path.length - 2, point);
      } else if(feature instanceof WRAP.Geo.Feature.Point) {
        if(feature.editable) {
          point = style.point;
        } else if(feature.permeate) {
          const index = feature.index;
          const features = this.layer.getDisplayList();
          for(const f of features) {
            if(f instanceof WRAP.Geo.Feature.GeoLine) {
              if(!isEqualFlightPlan(feature.props, f.props)) {
                continue;
              }
              if(f.index === index) {
                feature = f;
                point = [];
                const style = f.style();
                style.path.splice(1, style.path.length - 2, point);
                break;
              }
            }
          }
        }
      } else {
        return;
      }
    }

    if (!feature) {
      return;
    }

    this.layer.disableInteraction(true);
    this._onDragStart(feature);
    this._switchAutoMapScroll(true);
    set(clientX, clientY, point);
    dragFlg = true;

    let mouseupHandle;
    window.addEventListener('mousemove', mousemove, false);

    window.addEventListener('mouseup', mouseupHandle = event => {
      if(this.lockEvent) {
        return;
      }
      window.removeEventListener('mousemove', mousemove);
      window.removeEventListener('mouseup', mouseupHandle);
      this.mapDiv.classList.remove('drag');
      if(dragFlg) {
        dragFlg = false;
        this.lockEvent = true;
        this._onDragEnd(feature, point);
        this._switchAutoMapScroll(false);
        this.layer.disableInteraction(false);
      }
    }, false);
  }

  _switchAutoMapScroll(flg) {
    if(flg) {
      this.mousePosition = {};
      if(!(this.scrollTimer > 0)) {
        this.scrollTimer = setInterval(() => {
          if(this.mousePosition.x === undefined || this.mousePosition.y === undefined) {
            return;
          }
          const rect = this.mapDiv.getBoundingClientRect();
          const x = this.mousePosition.x - rect.left;
          const y = this.mousePosition.y - rect.top;
          let dx = 0;
          let dy = 0;
          const SCROLL_HIT_SIZE = 25;
          const SCROLL_SIZE = 4;
          if(x <= SCROLL_HIT_SIZE) {
            dx = -SCROLL_SIZE;
          } else if(rect.width - x <= SCROLL_HIT_SIZE) {
            dx = SCROLL_SIZE;
          }
          if(y <= SCROLL_HIT_SIZE) {
            dy = -SCROLL_SIZE;
          } else if(rect.height - y <= SCROLL_HIT_SIZE) {
            dy = SCROLL_SIZE;
          }
          if(dx !==0 || dy !==0) {
            const centerPoint = WRAP.Geo.getCenterPoint();
            const screenCenterPoint = WRAP.Geo.getScreenPoint(centerPoint);
            const movedCenterPoint = WRAP.Geo.getPoint(new WRAP.Geo.ScreenPoint(screenCenterPoint.x + dx, screenCenterPoint.y + dy));
            WRAP.Geo.setCenterPoint(movedCenterPoint);
            const mouseEvent = new MouseEvent("mousemove", {
              clientX: this.mousePosition.x,
              clientY: this.mousePosition.y
            });
            window.dispatchEvent(mouseEvent);
          }
        }, 17);
      }
    } else {
      if(this.scrollTimer > 0) {
        clearInterval(this.scrollTimer);
        this.scrollTimer = 0;
      }
    }
  }

  _onDragStart(feature) {
    this.dispatchHighlightPoints([]);
    this.editing = true;
    const index = feature.index;
    if(feature instanceof WRAP.Geo.Feature.Point && feature.editable) {
      this.originalPoint = Object.assign({}, this.detailTarget.routeList[index]);
      this.originalPoint.coordinate = this.originalPoint.coordinate.slice();
      const features = this.layer.getDisplayList();
      let a, b;
      for(const f of features) {
        if(f instanceof WRAP.Geo.Feature.GeoLine) {
          if(!isEqualFlightPlan(feature.props, f.props)) {
            continue;
          }
          if(f.index === index - 1) {
            a = f;
          } else if(f.index === index + 1) {
            b = f;
          }
        }
        if(a && b) break;
      }
      if(a && a.type === "airway") {
        const as = a.style();
        as.path = [this.detailTarget.routeList[index - 2].coordinate, this.detailTarget.routeList[index].coordinate];
      }
      if(b && b.type === "airway") {
        const bs = b.style();
        bs.path = [this.detailTarget.routeList[index].coordinate, this.detailTarget.routeList[index + 2].coordinate];
      }
      let style = feature.style();
      style.fillStyle = '#880';
    }
    this.labelLayer.setVisible(false);
    this.arrowLayer.setVisible(false);
  }

  _onDragEnd(feature, coordinate) {
    const index = feature.index;
    let mode;
    if(feature instanceof WRAP.Geo.Feature.GeoLine) {
      mode = 'line';
    } else if(feature instanceof WRAP.Geo.Feature.Point) {
      mode = 'point';
    }
    this._searchNearPoint(coordinate, mode, index);
  }

  _searchNearPoint(coordinate, mode, index) {
    const promises = [];
    const params = {
      lon: coordinate[0],
      lat: coordinate[1],
    };
    promises.push(this._getNearFix(params));
    promises.push(this._getNearLandmark(params));
    promises.push(this._getNearAirfield(params));
    Promise.all(promises)
      .then(refs => {
        const fixList = refs[0];
        const landmarkList = refs[1];
        const airfieldList = refs[2];
        const OFFSET_X = 50;
        const OFFSET_Y = 6;
        this.currentPopup = this._createFixListPopup(fixList, landmarkList, airfieldList, index, coordinate, mode);
        const pw = this.mapDiv.clientWidth;
        const ph = this.mapDiv.clientHeight;
        const bottomDiff = this.popupBaseDiv.clientHeight - ph;
        const rightDiff = this.popupBaseDiv.clientWidth - pw;
        const sp = WRAP.Geo.getScreenPoint(FPEUtils.convertWRAPPoint(coordinate[1], coordinate[0]));
        Object.assign(this.currentPopup.style, {
          visibility: 'hidden'
        });
        this.popupBaseDiv.appendChild(this.currentPopup);

        let left, right, top, bottom;
        if(pw / 2 > sp.x) {
          left = sp.x + OFFSET_X;
          if (pw - left < this.currentPopup.offsetWidth) {
            left = pw - this.currentPopup.offsetWidth;
          }
        } else {
          right = pw - sp.x + OFFSET_X + rightDiff;
          if (pw - right < this.currentPopup.offsetWidth) {
            right = pw - this.currentPopup.offsetWidth;
          }
        }
        if(ph / 2 > sp.y) {
          top = sp.y + OFFSET_Y;
        } else {
          bottom = ph - sp.y + OFFSET_Y + bottomDiff;
        }
        Object.assign(this.currentPopup.style, {
          left: left !== undefined ? `${left}px` : 'auto',
          right: right !== undefined ? `${right}px` : 'auto',
          top: top !== undefined ? `${top}px` : 'auto',
          bottom: bottom !== undefined ? `${bottom}px` : 'auto',
          visibility: 'visible'
        });
        overlay.addEventListener('click', this.overlayListenerFunc);
      })
      .catch(e => {
        this.errorFunc('Failed to retrieve FIX/Landmark', e, params);
        if (overlay) {
          this.overlayListenerFunc();
        }
      });

    const overlay = document.createElement('div');
    overlay.id = 'overlay';
    overlay.style.position = 'absolute';
    overlay.style.left = 0;
    overlay.style.top = 0;
    overlay.style.width = '100%';
    overlay.style.height = '100%';
    overlay.style.backgroundColor = 'rgba(128, 128, 128, 0.5)';
    overlay.style.zIndex = 1000;
    this.overlayListenerFunc = () => {
      overlay.remove();
      if(this.currentPopup !== null) {
        this.popupBaseDiv.removeChild(this.currentPopup);
        this.currentPopup = null;
      }
      this.mouseover = null;
      if (['dep', 'arr'].includes(mode)) {
        return;
      }
      if(this.editing) {
        if (this.draggingPoint) {
          this.dispatcher({type: 'END_DRAG'});
        }

        if(this.originalPoint) {
          this.detailTarget.routeList.splice(index, 1, this.originalPoint);
        }
        this.editing = false;
        this.originalPoint = null;
        this.labelLayer.setVisible(true);
        this.arrowLayer.setVisible(true);
      }
      this._makeFixes();
      this._redraw();
      this.lockEvent = false;
    }
    this.popupBaseDiv.appendChild(overlay);
  }

  _getNearFix(params) {
    const _params = {
      ...params,
      cycleDate: this.cycleDate
    };
    const apiUrl = process.env.REACT_APP_ARINC_API_URL;
    const apiKey = process.env.REACT_APP_ARINC_API_KEY;
    return axios.get(`${apiUrl}/near_fix`,
      {
        params: _params,
        headers: {
          'x-api-key': apiKey
        }
      })
      .then(response => response.data)
      .catch(e => {
        console.log('Failed to retrieve FIX', e);
        throw e;
      });
  }

  _getNearLandmark(params) {
    const config = {
      method: 'GET',
      params: params,
    };
    return executeLinksAPI('fosterlinks/customer_point/near', config);
  }

  _getNearAirfield(params) {
    const apiUrl = process.env.REACT_APP_NEAR_AIRFIELD_API_URL;
    const apiKey = process.env.REACT_APP_NEAR_AIRFIELD_API_KEY;

    const config = {
        url: `${apiUrl}/airfield/near`,
        headers: {
            'x-api-key': apiKey
        },
        method: 'GET',
        params: params
    };

    return axios(config)
        .then(res => {
          const list = [];
          const features = res.data?.features;
          if (features) {
            features.forEach(feature => {
              list.push({
                ...feature.properties,
                coordinates: [...feature.geometry.coordinates]
              });
            });
          }
          return list;
        }).catch(e => {
          throw e;
        });

  }

  _createFixListPopup(fixList, landmarkList, airfieldList, index, coordinate, mode) {
    const previewPoint = new WRAP.Geo.Feature.Point({
      point: [0,0],
      strokeStyle:'#000',
      lineWidth: 1,
      fillStyle: 'orange',
      pointSize: 10
    });
    const onMouseEnter = event => {
      const target = event.currentTarget;
      if(['fix', 'gps', 'landmark', 'airport', 'heliport'].includes(target.getAttribute("command"))) {
        previewPoint.style().point = [target.dataset.lon-0, target.dataset.lat-0];
        this.layer.addFeature(previewPoint);
        // WebGLの再描画条件
        this.layer._immediate = true;
        WRAP.Geo.invalidate();
      }
    };
    const onMouseLeave = event => {
      this.layer.removeFeature(previewPoint);
      // WebGLの再描画条件
      this.layer._immediate = true;
      WRAP.Geo.invalidate();
    };
    const onClick = event => {
      const overlay = document.getElementById('overlay');
      overlay.removeEventListener('click', this.overlayListenerFunc);
      const target = event.currentTarget;
      const command = target.getAttribute("command");
      //DEP,ARR選択時
      if (['dep', 'arr'].includes(mode)) {
        if (command !== 'cancel') {
          const selected = {
            coordinate: [target.dataset.lon-0, target.dataset.lat-0]
          };
          if(['fix', 'landmark', 'airport', 'heliport'].includes(command)) {
            selected.name = target.dataset.ident;
          } else if(command === 'gps') {
            selected.name = FPEUtils.formatLonLatATC1.apply(null, selected.coordinate);
          }

          let targetPoint;
          let dist = 0;
          if (mode === 'dep') {
            targetPoint = this.detailTarget.routeList[0];
            const afterPoint = this.detailTarget.routeList[1];
            if (afterPoint) {
              afterPoint.dist = this._getDistance(selected, afterPoint);
            }

            if (this.depArrFeatures.length === 3) {
              this._clearDepArrFeature(false);
            } else if (this.depArrFeatures.length !== 0) {
              const style = this.depArrFeatures[0].style();
              style.point = selected.coordinate;
              const labelStyle = this.depArrLabelFeatures[0].style();
              labelStyle.point = selected.coordinate;
              labelStyle.text = selected.name;
            }
          } else {
            targetPoint = this.detailTarget.routeList[this.detailTarget.routeList.length - 1];
            const beforePoint = this.detailTarget.routeList[this.detailTarget.routeList.length - 2];
            if (beforePoint) {
              dist = this._getDistance(beforePoint, selected);
            }
            this._clearDepArrFeature(false);
          }

          Object.assign(targetPoint, {
            coordinate: selected.coordinate,
            name: selected.name,
            dist,
          });
          this.dispatcher({type: 'EDIT', payload: this.detailTarget});
        }
        this.overlayListenerFunc();
        return;
      }

      //ルート編集時
      const trimAirway = index => {
        if(index < 0 || index >= this.detailTarget.routeList.length) {
          return false;
        }
        if(this.detailTarget.routeList[index].type === "airway") {
          this.detailTarget.routeList.splice(index, 1);
          return true;
        }
        return false;
      }
      const copyProperties = (copy, from) => {
        ["level", "speed", "levelOpt"].forEach(key => {
          if(key in from) {
            copy[key] = from[key];
          }
        });
      }
      let currentIndex = index;
      let currentPoint;
      let fix1 = null;
      let fix2 = null;
      let type = null;
      if(command === 'del') {
        fix1 = this._nextFix(currentIndex, -1);
        this.detailTarget.routeList.splice(currentIndex, 1);
        currentPoint = this._nextFix(currentIndex - 1, 1);
      } else if(['fix', 'gps', 'landmark', 'airport', 'heliport'].includes(command)) {
        currentPoint = {};
        currentPoint.coordinate = [target.dataset.lon-0, target.dataset.lat-0];
        if(['fix', 'landmark', 'airport', 'heliport'].includes(command)) {
          currentPoint.name = target.dataset.ident;
        } else if(command === 'gps') {
          currentPoint.name = FPEUtils.formatLonLatATC1.apply(null, currentPoint.coordinate);
        }
        fix1 = this._nextFix(currentIndex, -1);
        fix2 = this._nextFix(currentIndex + (mode === 'line' ? -1 : 0), 1);
        if (mode === 'point') {
          const current = this._nextFix(currentIndex - 1, 1);
          currentPoint.alt = current.alt;
          currentPoint.transitTime = current.transitTime;
        } else {
          currentPoint.alt = fix1 && fix1.alt !== undefined ? fix1.alt : (fix2 && fix2.alt !== undefined ? fix2.alt : 0)
        }
        type = command;
      } else {
        // cancel
        this.overlayListenerFunc();
        return;
      }
      const params = {
        fix: currentPoint.name,
        lon: currentPoint.coordinate[0],
        lat: currentPoint.coordinate[1],
        alt: currentPoint.alt,
      };
      if(fix1) {
        Object.assign(params, {
          fix1: fix1.name,
          lon1: fix1.coordinate[0],
          lat1: fix1.coordinate[1],
          alt1: fix1.alt,
        });
      }
      if(fix2) {
        Object.assign(params, {
          fix2: fix2.name,
          lon2: fix2.coordinate[0],
          lat2: fix2.coordinate[1],
          alt2: fix2.alt,
        });
      }
      Object.assign(params, { cycleDate: this.cycleDate });
      const apiUrl = process.env.REACT_APP_ARINC_API_URL;
      const apiKey = process.env.REACT_APP_ARINC_API_KEY;
      axios.get(`${apiUrl}/plan_route`,
      {
        params: params,
        headers: {
          'x-api-key': apiKey
        }
      })
      .then(response => response.data)
      .then(data => {
        if(mode === 'line') {
          if(this.detailTarget.routeList[currentIndex].type === 'airway') {
            this.detailTarget.routeList[currentIndex] = {};
          } else if (this.draggingPoint && this.draggingPoint.data.temporary) {
            const idx = this.draggingPoint.data.index;
            const temporaryIdx = this.draggingPoint.data.temporaryIdx;
            const temporaryPoints = this.detailTarget.routeList[idx].temporaryPoints;
            const part1 = temporaryPoints.slice(0, temporaryIdx);
            const part2 = temporaryPoints.slice(temporaryIdx + 1);
            if (part1.length > 0) {
              this.detailTarget.routeList[idx].temporaryPoints = part1;
            } else {
              delete this.detailTarget.routeList[idx].temporaryPoints;
            }

            const p = {};
            if (part2.length > 0) {
              p.temporaryPoints = part2;
            }
            this.detailTarget.routeList.splice(currentIndex, 0, p);

          } else {
            this.detailTarget.routeList.splice(currentIndex, 0, {});
          }
        } else {
          let offset = 1;
          if(command === 'del') {
            offset = 0;
          }
          trimAirway(currentIndex - 1) && currentIndex--;
          trimAirway(currentIndex + offset);
        }

        if(command !== 'del') {
          const before = this.detailTarget.routeList[currentIndex];
          if (['dep', 'arr'].includes(before.type)) {
            Object.assign(before, {
              coordinate: currentPoint.coordinate,
              name: currentPoint.name,
              transitTime: currentPoint.transitTime,
            });
          } else {
            Object.assign(before, {
              coordinate: currentPoint.coordinate,
              name: currentPoint.name,
              type: type,
              transitTime: currentPoint.transitTime,
            });
          }
        }

        data.forEach((v, i, list) => {
          let _index = currentIndex + i;
          let currentRoute = this.detailTarget.routeList[_index];
          let beforeRoute = this.detailTarget.routeList[_index - 1];
          let afterRoute = this.detailTarget.routeList[_index + 1];
          let afterRoute2 = this.detailTarget.routeList[_index + 2];
          if(v.airway) {
            if (currentIndex === 0) {
              _index = 1 + i;
              currentRoute = this.detailTarget.routeList[_index];
              beforeRoute = this.detailTarget.routeList[_index - 1];
              afterRoute = this.detailTarget.routeList[_index + 1];
              afterRoute2 = this.detailTarget.routeList[_index + 2];
            }
            this.detailTarget.routeList.splice(_index, 0, {
              name: v.airwayName,
              type: "airway",
              props: currentRoute.props !== undefined ? currentRoute.props : beforeRoute.props,
              fixes: v.airway.map((v, j, a) => {
                let dist;
                let transitTime;
                if (j === 0) {
                  dist = beforeRoute?.dist;
                  transitTime = beforeRoute?.transitTime;
                } else {
                  dist = this._getDistance(a[j - 1], v);
                  if (j === a.length - 1) {
                    currentRoute.dist = dist;
                    if (afterRoute && afterRoute.type === 'airway') {
                      afterRoute.fixes[0].dist = dist;
                    }
                    transitTime = currentRoute?.transitTime;
                  }
                }
                const ret = {
                  type: "airway-fix",
                  coordinate: [v.lon-0, v.lat-0],
                  name: v.name,
                  transitTime: transitTime,
                  alt: beforeRoute.alt,
                  dist,
                  minAlt: v.minAlt,
                  maxAlt: v.maxAlt,
                };
                if(j === a.length - 1) {
                  if(currentRoute.alt !== undefined) {
                    ret.alt = currentRoute.alt;
                  }
                }
                return ret;
              }),
              refs: {
                airwayName: v.airwayName,
                airway: v.airway.map(a => {
                  let o = Object.assign(a);
                  copyProperties(o, beforeRoute.refs);
                  return o;
                })
              },
            });
            currentIndex++;
          } else {
            if (beforeRoute) {
              currentRoute.dist = this._getDistance(beforeRoute, currentRoute);
            } else {
              currentRoute.dist = 0;
              if (afterRoute) {
                afterRoute.dist = this._getDistance(currentRoute, afterRoute);
                if (afterRoute2 && afterRoute2.type === 'airway') {
                  afterRoute2.fixes[0].dist = afterRoute.dist;
                }
              }
            }
            if (afterRoute && afterRoute.type === 'airway') {
              afterRoute.fixes[0].dist = currentRoute.dist;
            }
          }
          if(i === 0) {
            if(currentRoute.alt === undefined) {
              currentRoute.alt = beforeRoute.alt;
            }
            if(currentRoute.props === undefined) {
              currentRoute.props = beforeRoute.props;
            }
            if(!currentRoute.refs) {
              currentRoute.refs = {};
            }
            if (beforeRoute && beforeRoute.refs) {
              copyProperties(currentRoute.refs, beforeRoute.refs);
            }
          }
        });

        this.editing = false;
        this.originalPoint = null;
        this.labelLayer.setVisible(true);
        this.arrowLayer.setVisible(true);
        document.getElementById('overlay').remove();
        this.lockEvent = false;

        this.dispatcher({type: 'EDIT', payload: this.detailTarget});
      })
      .catch(e => {
        this.errorFunc('Failed to apply the route', e, params);
        this.overlayListenerFunc();
      });
      this.popupBaseDiv.removeChild(this.currentPopup);
      this.currentPopup = null;
    };

    const labels = [];
    labels.push({ label: FPEUtils.translation('Registration point') });
    labels.push({ label: FPEUtils.translation('Airport') });
    labels.push({ label: 'FIX' });

    const landmarks = [];
    landmarkList.forEach(d => {
      let img;
      if (d.iconId > 0 && d.iconId <= 100) {
        let svg;
        if (d.iconId === 1) {  //スクエア
          svg = <ImageSquare />;
        } else if (d.iconId === 2) {  //スポット
          svg = <ImageSpot />;
        } else if (d.iconId === 3) {  //病院
          svg = <ImageHospital />;
        } else if (d.iconId === 4) {  //ヘリポート
          svg = <ImageHeliport />;
        } else if (d.iconId === 5) {  //発電所
          svg = <ImageSubstation />;
        }
        const style = { verticalAlign: "middle", height: "16px" };
        if (d.color) {
          style.color = d.color;
        }
        img = <SvgIcon viewBox="0 0 512 512" style={style}>{svg}</SvgIcon>;
      } else if (d.path) {
        img = <img src={d.path} style={{width: '16px', height: '16px', padding: '0 4px'}} alt={d.name} />;
      } else {
        const style = { verticalAlign: "middle", height: "16px" };
        img = <SvgIcon viewBox="0 0 512 512" style={style}><ImageSquare /></SvgIcon>;
      }

      landmarks.push(<li
        key={d['pointId']}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={onClick}
        command="landmark"
        data-ident={d['name']}
        data-lon={d['lon']}
        data-lat={d['lat']}
      ><ul className="options"><li className="command-landmark">{img}</li><li>{d['name']}</li></ul></li>);
    });

    const nameStyle = {
      maxWidth: 350,
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      display: 'inline-block'
    };
    const fixes = [];
    fixList.forEach(d => {
      const name = !d['airway'] ? d['ident'] : `${d['ident']} (${d['airway']})`;
      fixes.push(<li
        key={d['ident']}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={onClick}
        command="fix"
        data-ident={d['ident']}
        data-lon={d['lon']}
        data-lat={d['lat']}
      ><ul className="options">
        <li className="command">FIX</li>
        <li style={nameStyle} title={name}>{name}</li>
      </ul></li>);
    });

    const airfields = [];
    airfieldList.forEach((d, airfieldIdx) => {
      const isAirport = d['type'] === 'Airport';
      const name = d['name'];
      const coordinates = d['coordinates'];
      airfields.push(<li
        key={`airfield_${airfieldIdx}`}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={onClick}
        command={isAirport ? "airport" : "heliport"}
        data-ident={name}
        data-lon={coordinates[0]}
        data-lat={coordinates[1]}
      ><ul className="options">
        <li className="command-airfield">{d['type']}</li>
        <li style={nameStyle} title={name}>{name}</li>
      </ul></li>);
    });

    //GPS, DEL(or Cancel)
    const lonlatStr = FPEUtils.formatLonLatDM(coordinate[0], coordinate[1]);
    const gps = <li
      key={lonlatStr}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      command="gps"
      data-ident={lonlatStr}
      data-lon={coordinate[0]}
      data-lat={coordinate[1]}
    ><ul className="options"><li className="command">GPS</li><li>{lonlatStr}</li></ul></li>;
    const deletable = mode === 'point' ? index !== 0 && index !== this.detailTarget.routeList.length - 1 : false;
    const del = <li
      key={"delete"}
      onClick={onClick}
      command={deletable ? 'del' : 'cancel'}
    ><ul className="options"><li className="command">{deletable ? 'DEL' : 'CXL'}</li><li>{deletable ? 'Delete Waypoint' : 'Cancel'}</li></ul></li>;
    landmarks.push(gps);
    landmarks.push(del);
    fixes.push(gps);
    fixes.push(del);
    airfields.push(gps);
    airfields.push(del);

    let defaultTabIdx = 0;
    if (landmarkList.length === 0) {
      defaultTabIdx = airfieldList.length > 0 ? 1 : 2;
    }
    const popup = document.createElement('div');
    popup.id = 'popup';
    ReactDOM.render(
      <>
        <div className="title">{FPEUtils.translation('Location Information')}</div>
        <CustomTab variant="fullWidth" labels={labels} defaultValue={defaultTabIdx}>
          <ul className="options_root">
            {landmarks}
          </ul>
          <ul className="options_root">
            {airfields}
          </ul>
          <ul className="options_root">
            {fixes}
          </ul>
        </CustomTab>
      </>
    , popup);
    return popup;
  }

  _redraw() {
    if(!this.flightPlanList) {
      return;
    }

    this.layer.clear();
    this.flightPlanLabelList = [];

    const lineFeatures = [];
    const pointFeatures = [];
    const airwayPointFeatures = [];

    for(let i = this.flightPlanList.length - 1; i >= 0; i--) {
      const fp = this.flightPlanList[i];
      if (!isEqualFlightPlan(this.detailTarget, fp)) {
        this._makeFeatures(fp, lineFeatures, pointFeatures, airwayPointFeatures);
      }
    }

    if (this.detailTarget) {
      let targetStyle;
      if (this.editMode) {
        targetStyle = {
          line: { strokeStyle: "rgba(224, 64, 208, 0.5)", lineWidth: 7 },
          point: { strokeStyle: '#008', fillStyle: "rgba(224, 64, 208, 0.5)" },
        };
      } else {
        targetStyle = {
          line: { strokeStyle: "rgba(224, 64, 208, 0.75)", lineWidth: 4 },
          point: { strokeStyle: '#008', fillStyle: "rgba(224, 64, 208, 0.75)" },
        };
      }
      this._makeFeatures(this.detailTarget, lineFeatures, pointFeatures, airwayPointFeatures, targetStyle);
    }

    // Fix Label
    this.flightPlanFixList.reverse().forEach((fixList, planIdx) => {
      fixList.forEach((fix, idx, org) => {
        const p1 = idx !== 0 ? org[idx - 1] : org[0];
        const p2 = idx !== org.length - 1 ? org[idx + 1] : org[org.length - 1];
        const angle = FPEUtils.calcMiddleAngle(fix.coordinate, p1.coordinate, p2.coordinate);
        this.flightPlanLabelList[planIdx].fix.push({
          type: fix.type,
          route: fix,
          text: fix.name,
          point: fix.coordinate,
          angle: angle,
          index: idx
        });
      });
    });

    lineFeatures.forEach(f => this.layer.addFeature(f));
    airwayPointFeatures.forEach(f => this.layer.addFeature(f));
    pointFeatures.forEach(f => this.layer.addFeature(f));

    this._redrawLabel();
    this._redrawArrow();

    this.depArrFeatures.forEach(f => this.layer.addFeature(f));
    this.depArrLabelFeatures.forEach(f => this.layer.addFeature(f));

    this.layer._immediate = true;
    WRAP.Geo.invalidate();
  }

  _makeFeatures(fp, lineFeatures, pointFeatures, airwayPointFeatures, customStyle={}, skipLabel=false) {
    const routeList = fp.routeList;
    //name,緯度経度が2点(dep,arr)揃っていないと対象としない
    if (routeList.length < 2
      || !routeList[0].name || routeList[0].coordinate.filter(v => v !== null).length !== 2
      || !routeList[routeList.length-1].name || routeList[routeList.length-1].coordinate.filter(v => v !== null).length !== 2) {
      return;
    }

    const labelList = {fix:[], dist:[]};

    for(let i = 0; i < routeList.length; i++) {
      const currentRoute = routeList[i];
      if(i > 0) {
        const beforeRoute = routeList[i - 1];

        // Route Line
        let coordinate = null;
        if(['dep', 'fix', 'gps', 'gmap', 'plan', 'landmark', 'airport', 'heliport'].includes(beforeRoute.type)
          && ['arr', 'fix', 'gps', 'gmap', 'plan', 'landmark', 'airport', 'heliport'].includes(currentRoute.type)) {
          coordinate = [ beforeRoute.coordinate, currentRoute.coordinate ];
        } else if(currentRoute.type === 'airway') {
          coordinate = currentRoute.fixes.map(f => f.coordinate);
        }
        if(coordinate !== null) {
          const lineStyle = deepmerge({
            strokeStyle: fp.hasUnknownPoint ? "rgba(255, 0, 0, 0.75)" : "rgba(0, 0, 255, 0.75)",
            lineWidth: 2,
            method: "GC"
          }, customStyle.line || {});
          lineStyle.path = coordinate;
          const lineFeture = new WRAP.Geo.Feature.GeoLine(lineStyle);
          lineFeture.draggable = true;
          lineFeture.index = i;
          lineFeture.type = currentRoute.type;
          lineFeture.editable = true;
          lineFeture.props = currentRoute.props;
          lineFeatures.push(lineFeture);
        }

        if (!skipLabel) {
          // Distance Label
          let distance;
          coordinate = null;
          if(['dep', 'fix', 'gps', 'gmap', 'plan', 'landmark', 'airport', 'heliport'].includes(beforeRoute.type)
            && ['arr', 'fix', 'gps', 'gmap', 'plan', 'landmark', 'airport', 'heliport'].includes(currentRoute.type)) {
            distance = currentRoute.dist;
            coordinate = currentRoute.coordinate;
          } else if(currentRoute.type === 'airway') {
            distance = currentRoute.dist;
            coordinate = currentRoute.fixes[currentRoute.fixes.length - 1].coordinate;
          }
          if(getCustomerSettings("PlanRoute.Distance") && distance !== undefined) {
            labelList.dist.push({type: "distance", route: currentRoute, text: this._toDistString(distance), point: coordinate});
          }
        }
      }

      // Route Point
      if(['dep', 'arr', 'fix', 'gps', 'gmap', 'plan', 'landmark', 'airport', 'heliport'].includes(currentRoute.type)) {
        const pointStyle = deepmerge({
          strokeStyle: fp.hasUnknownPoint ? '#800' : '#008',
          lineWidth: 1,
          fillStyle: fp.hasUnknownPoint ? 'rgba(255, 0, 0, 0.2)' : '#FFF',
          pointSize: 9
        }, customStyle.point || {});
        pointStyle.point = currentRoute.coordinate;
        const pointFeture = new WRAP.Geo.Feature.Point(pointStyle);
        pointFeture.index = i;
        pointFeture.data = currentRoute;
        pointFeture.props = currentRoute.props;
        pointFeatures.push(pointFeture);
        if(fp.isLinksPlan || !['dep', 'arr'].includes(currentRoute.type)) {
          pointFeture.editable = true;
        }
      } else if(currentRoute.type === 'airway') {
        for(let j = 1; j < currentRoute.fixes.length - 1; j++) {
          const aiwaryPointStyle = deepmerge({
            pointSize: 8,
            strokeStyle: false,
            fillStyle: "#888"
          }, customStyle.airwayPoint || {});
          aiwaryPointStyle.point = currentRoute.fixes[j].coordinate;
          const pointFeture = new WRAP.Geo.Feature.Point(aiwaryPointStyle);
          pointFeture.index = i;
          pointFeture.data = currentRoute.fixes[j];
          pointFeture.permeate = true;
          pointFeture.props = currentRoute.props;
          airwayPointFeatures.push(pointFeture);
        }
      }
    }

    if (!skipLabel) {
      this.flightPlanLabelList.push(labelList);
    }
  }

  _redrawLabel() {
    if(!this.flightPlanLabelList) {
      return;
    }
    const fontSize = 12;
    this.labelLayer.clear();
    // const mask = WRAP.Geo.getMask();
    const mask = new WRAP.Geo.Mask(WRAP.Geo.currentView());
    mask.clear();
    const features = [];
    const make = (l, type, deparr) => {
      if(!l || !l.text) {
        return;
      }
      let flg = false;
      const textSize = WRAP.Geo.getTextMetrics(l.text, fontSize);
      const w = textSize[0];
      const h = textSize[1];
      const candidates = (() => {
        let angle = 90;
        if(l.angle !== undefined) {
          angle = FPEUtils.normalizeDegree(l.angle);
          if(deparr) {
            if(angle > 180) {
              angle = 90;
            } else if(angle <= 90) {
              angle = 180;
            } else {
              angle = 0;
            }
          } else {
            while(angle > 180) {
              angle -= 180;
            }
          }
        }
        if(type === 'fix') {
          let candidates;
          if(angle < 90) {
            candidates = [[angle, 10], [angle - 45, 10], [angle + 45, 10]];
          } else {
            candidates = [[angle, 10], [angle + 45, 10], [angle - 45, 10]];
          }
          return candidates.filter(v => v[0] >= 0 && v[0] <= 180);
        } else {
          return [[-90, 10]];
        }
      })();
      let x, y;
      let tx, ty;
      const margin = 2;
      const fixY = -2; //Y軸は少し浮いて見えるため調整
      for(let i = 0; i < candidates.length; i++) {
        const candidate = candidates[i];
        const r = candidate[1];
        const deg = FPEUtils.normalizeDegree(candidate[0]);
        const rad = FPEUtils.deg2rad(deg);
        x = Math.cos(rad) * r;
        y = Math.sin(rad) * r;
        if(deg >= 0 && deg <= 60) {
          tx = x + margin;
          ty = y + h / 2 + fixY;
        } else if(deg > 60 && deg < 120) {
          tx = x - w / 2;
          ty = y + h + margin + fixY;
        } else if(deg >= 120 && deg <= 180) {
          tx = x - w - margin;
          ty = y + h / 2 + fixY;
        } else {
          tx = x - w / 2;
          ty = y + margin + fixY;
        }
        if(deparr || !mask.intersectRect(l.point, tx, ty, w, h)) {
          flg = true;
          mask.addRect(l.point, tx, ty, w, h);
          break;
        }
      }

      if(flg) {
        const feature = new WRAP.Geo.Feature.Text({
          point: l.point,
          text: l.text,
          fontSize: fontSize,
          fillStyle: this.isDark ? "#FFF" : "#000",
          offsetX: tx,
          offsetY: ty,
          align: "left"
        });
        if(type === 'fix') {
          features.push(new WRAP.Geo.Feature.Line({
            point: l.point,
            line: [[0, 0], [x, y]],
            lineWidth: 1,
            strokeStyle: 'rgba(50,50,50,0.8)'
          }));
          feature.index = l.index;
          feature.data = l.route;
          feature.props = l.route.props;
        }
        features.push(feature);
      }
    }

    this.flightPlanLabelList.reverse().forEach(labelList => {
      // dep/arr は必須
      if(labelList.fix.length >= 1) {
        make(labelList.fix[0], 'fix', true);
      }
      if(labelList.fix.length >= 2) {
        make(labelList.fix[labelList.fix.length - 1], 'fix', true);
      }

      const lowPriority = [];
      for(let i = 1; i < labelList.fix.length - 1; i++) {
        if(labelList.fix[i].type === 'airway-fix') {
          lowPriority.push(labelList.fix[i]);
        } else {
          make(labelList.fix[i], 'fix');
        }
      }
      lowPriority.forEach(label => make(label, 'fix'));

      labelList.dist.forEach(label => make(label, 'dist'));
    });

    features.reverse().forEach(f => this.labelLayer.addFeature(f));
  }

  _redrawArrow() {
    if(!this.flightPlanList) {
      return;
    }
    this.arrowLayer.clear();

    const mask = new WRAP.Geo.Mask(WRAP.Geo.currentView());
    mask.clear();
    let features = [];
    if (this.detailTarget) {
      features = features.concat(this._makeArrowFeatures(this.detailTarget, mask));
    }
    this.flightPlanList.forEach(plan => {
      if (isEqualFlightPlan(this.detailTarget, plan)) {
        return;
      }
      features = features.concat(this._makeArrowFeatures(plan, mask));
    });

    features.reverse().forEach(f => this.arrowLayer.addFeature(f));
  }

  _isOutOfScreen(sp) {
    const mapSize = WRAP.Geo.getSize();
    return sp.x < 0 || sp.x > mapSize.width || sp.y < 0 || sp.y > mapSize.height;
  };

  _getIntersection(line1, line2) {
    const x0 = line1[0][0];
    const y0 = line1[0][1] * -1;
    const x1 = line1[1][0];
    const y1 = line1[1][1] * -1;
    const x2 = line2[0][0];
    const y2 = line2[0][1] * -1;
    const x3 = line2[1][0];
    const y3 = line2[1][1] * -1;

    const v1 = x1 - x0 === 0;
    const v2 = x3 - x2 === 0;
    if (v1 && v2) {
      return false; //平行(縦)
    } else if (v1) {
      const a = (y3 - y2) / (x3 - x2);
      const b = y2 - a * x2;
      const x = x0;
      if (x > Math.max(x2, x3) || x < Math.min(x2, x3)) {
        return false;
      }
      const y = a * x + b;
      if (y > Math.max(y0, y1) || y < Math.min(y0, y1)) {
        return false;
      }
      return { x : x, y : y * -1 };
    } else if (v2) {
      const a = (y1 - y0) / (x1 - x0);
      const b = y0 - a * x0;
      const x = x2;
      if (x > Math.max(x0, x1) || x < Math.min(x0, x1)) {
        return false;
      }
      const y = a * x + b;
      if (y > Math.max(y2, y3) || y < Math.min(y2, y3)) {
        return false;
      }
      return { x : x, y : y * -1 };
    }

    const a0 = (y1 - y0) / (x1 - x0);
    const a1 = (y3 - y2) / (x3 - x2);
    if (Math.abs(a0) === Math.abs(a1)) return false; //平行(横)

    const x = (a0 * x0 - y0 - a1 * x2 + y2) / (a0 - a1);
    if (
      x > Math.max(x0, x1) || x < Math.min(x0, x1) ||
      x > Math.max(x2, x3) || x < Math.min(x2, x3)
    ) return false;

    if (a0 === 0 || a1 === 0) {
      return { x : x, y : a0 === 0 ? line1[0][1] : line2[0][1] };
    }

    const y = (y1 - y0) / (x1 - x0) * (x - x0) + y0;
    if (
      y > Math.max(y0, y1) || y < Math.min(y0, y1) ||
      y > Math.max(y2, y3) || y < Math.min(y2, y3)
    ) return false;

    return { x : x, y : y * -1 };
  };

  _makeArrowFeatures(plan, mask) {
    const arrowFeatures = [];
    //name,緯度経度が2点(dep,arr)揃っていないと対象としない
    if (!plan || !plan.routeList || plan.routeList.length < 2
      || !plan.routeList[0].name || plan.routeList[0].coordinate.filter(v => v !== null).length !== 2
      || !plan.routeList[plan.routeList.length-1].name || plan.routeList[plan.routeList.length-1].coordinate.filter(v => v !== null).length !== 2) {
      return arrowFeatures;
    }

    const w = 4;
    const h = 6;
    const mapSize = WRAP.Geo.getSize();
    const list = plan.routeList.flatMap(p=>p.type==="airway" ? p.fixes.slice(1, p.fixes.length -1) : p);
    for (let i = 0; i < list.length; i++) {
      if (i === list.length - 1) continue;

      const p1 = list[i].coordinate;
      const p2 = list[i + 1].coordinate;
      const p1xy = WRAP.Geo.getScreenPoint(FPEUtils.convertWRAPPoint(p1[1], p1[0]));
      const p2xy = WRAP.Geo.getScreenPoint(FPEUtils.convertWRAPPoint(p2[1], p2[0]));

      const gc = new WRAP.Geo.GreatCircle(p1[1], p1[0], p2[1], p2[0]);
      let flg = false;
      let point;
      let rotation;
      const candidates = (function() {
        const outOfScreenP1 = this._isOutOfScreen(p1xy);
        const outOfScreenP2 = this._isOutOfScreen(p2xy);
        let _p1xy = p1xy;
        let _p2xy = p2xy;
        let intersect = false;
        if (outOfScreenP1 || outOfScreenP2) {
          const line = [[p1xy.x, p1xy.y], [p2xy.x, p2xy.y]];
          const bounds = [
            [[0,0], [mapSize.width,0]],
            [[mapSize.width,0], [mapSize.width,mapSize.height]],
            [[mapSize.width,mapSize.height], [0,mapSize.height]],
            [[0,mapSize.height], [0,0]]
          ];
          const intersections = [];
          bounds.forEach((b) => {
            const ret = this._getIntersection(line, b);
            if (ret !== false) {
              intersect = true;
              intersections.push(ret);
            }
          });
          if (intersections.length === 2) {
            _p1xy = intersections[0];
            _p2xy = intersections[1];
          } else if (intersections.length === 1) {
            if (outOfScreenP1) {
              _p1xy = intersections[0];
            } else {
              _p2xy = intersections[0];
            }
          }
        }

        //画面内の線分の長さが一定未満の場合は空配列を返却(矢印は描画されない)
        if (Math.pow(_p1xy.x - _p2xy.x, 2) + Math.pow(_p1xy.y - _p2xy.y, 2) < Math.pow(w + h * 2, 2)) {
          return [];
        }

        let candidates = [0.5];
        //画面内の線分の長さが一定超過の場合は描画位置候補を3点とする
        if (Math.pow(_p1xy.x - _p2xy.x, 2) + Math.pow(_p1xy.y - _p2xy.y, 2) > Math.pow((w + h * 2) * 4, 2)) {
          candidates = [0.5, 0.25, 0.75];
        }
        if (!intersect) {
          return candidates;
        } else {
          const adjusted = [];
          candidates.forEach((c) => {
            let candidate;
            if (p2xy.x - p1xy.x !== 0) {
              const x = _p1xy.x + (_p2xy.x - _p1xy.x) * c;
              candidate = (x - p1xy.x)/(p2xy.x - p1xy.x);
            } else {
              const y = _p1xy.y + (_p2xy.y - _p1xy.y) * c;
              candidate = (y - p1xy.y)/(p2xy.y - p1xy.y);
            }
            adjusted.push(candidate);
          });
          return adjusted;
        }
      }.bind(this))();

      for (let j = 0; j < candidates.length; j++) {
        const t = candidates[j];
        const pt = gc.getPosition(t);
        point = [pt.lon, pt.lat];
        const xy  = WRAP.Geo.getScreenPoint(FPEUtils.convertWRAPPoint(pt.lat, pt.lon));
        if (this._isOutOfScreen(xy)) {
          continue;
        }
        const pt1 = gc.getPosition(t - 0.1);
        const pt2 = gc.getPosition(t + 0.1);
        const xy1 = WRAP.Geo.getScreenPoint(FPEUtils.convertWRAPPoint(pt1.lat, pt1.lon));
        const xy2 = WRAP.Geo.getScreenPoint(FPEUtils.convertWRAPPoint(pt2.lat, pt2.lon));
        const angle1 = Math.atan2(xy1.y - xy.y, xy1.x - xy.x);
        const angle2 = Math.atan2(xy.y - xy2.y, xy.x - xy2.x);
        const rotationRad = Math.atan2(
          Math.sin(angle1) + Math.sin(angle2),
          Math.cos(angle1) + Math.cos(angle2)
        );
        rotation = FPEUtils.rad2deg(rotationRad);
        const rect = this._getBoundingRect(w, h, rotation);
        if (!mask.intersectRect(point, rect[0], rect[1], rect[2], rect[3])) {
          flg = true;
          mask.addRect(point, rect[0], rect[1], rect[2], rect[3]);
          break;
        }
      }
      if (!flg) continue;

      arrowFeatures.push(new WRAP.Geo.Feature.Line({
        point: point,
        line: this._makeArrowSymbol(w, h),
        strokeStyle: "#000",
        fillStyle: "#ffff00",
        rotation: rotation,
      }));
    }
    return arrowFeatures;
  }

  _makeArrowSymbol(w, h) {
    const vp = 1;
    const hp = 5;
    const aw = h + 10;
    const ah = aw / 2;
    let r = 1;

    const array = [
      [(-w / 2 - hp) * r, -h / 2 - vp], [(w / 2 + hp) * r, -h / 2 - vp],
      [(w / 2 + hp) * r, h / 2 + vp], [(-w / 2 - hp) * r, h / 2 + vp],
    ];
    array.push(
      [(-w / 2 - hp) * r, aw / 2], [(-w / 2 - hp - ah) * r, 0], [(-w / 2 - hp) * r, -aw / 2]
    );
    array.push(array.slice(0, 1)[0]);
    return array;
  }

  _getBoundingRect(w, h, rotation) {
    const array = this._makeArrowSymbol(w, h, "");
    const rad = FPEUtils.deg2rad(rotation);
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    let minX, minY, maxX, maxY;
    array.forEach(v => {
      const x = v[0] * cos - v[1] * sin;
      const y = v[0] * sin + v[1] * cos;
      if (minX === undefined || minX > x) {
        minX = x;
      }
      if (maxX === undefined || maxX < x) {
        maxX = x;
      }
      if (minY === undefined || minY > y) {
        minY = y;
      }
      if (maxY === undefined || maxY < y) {
        maxY = y;
      }
    });
    return ([
      minX,
      minY,
      maxX - minX,
      maxY - minY
    ]);
  }

  _toDistString(meter) {
    switch(getCustomerSettings("PlanRoute.DistanceUnit")) {
      case 'km':
      default:
        return (
          Math.round(meter / 1000) + "km"
        );
      case 'nm':
        return (
          Math.round(meter / 1852.0) + "nm"
        );
    }
  }

  _isDepArrSelected() {
    if (!this.visible || !this.editMode || !this.detailTarget || !this.detailTarget.isLinksPlan) {
      return null;
    }
    const dep = this.detailTarget.routeList[0];
    const arr = this.detailTarget.routeList[this.detailTarget.routeList.length - 1];
    const depSelected = dep.coordinate.filter(v => v !== null).length === 2;
    const arrSelected = arr.coordinate.filter(v => v !== null).length === 2;
    return {
      selected: depSelected && arrSelected,
      depSelected,
      arrSelected,
      dep,
      arr
    };
  }

  _makeDepArrFeature(coordinate) {
    return new WRAP.Geo.Feature.Point({
      point: coordinate,
      strokeStyle: 'rgba(0, 0, 136, 0.7)',
      lineWidth: 2,
      fillStyle: 'rgba(224, 64, 208, 0.7)',
      pointSize: 12
    });
  }

  _labelFillStyle() {
    return this.isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 136, 0.7)';
  }

  _makeDepArrLabelFeature(coordinate, text) {
    return new WRAP.Geo.Feature.Text({
      point: coordinate,
      text: text,
      fontSize: 12,
      fillStyle: this._labelFillStyle(),
      offsetY: 24,
      align: "center"
    });
  }

  _makeDepArrGeoLineFeature(p1, p2) {
    return new WRAP.Geo.Feature.GeoLine({
      path: [p1, p2],
      strokeStyle: "rgba(224, 64, 208, 0.5)",
      lineWidth: 7,
      method: "GC"
    });
  }

  _redrawDepArrFeature(sp) {
    const depArrSelected = this._isDepArrSelected();
    if (depArrSelected && !depArrSelected.selected) {
      const rect = this.mapDiv.getBoundingClientRect();
      const x = sp.x - rect.left;
      const y = sp.y - rect.top;
      const currentPoint = WRAP.Geo.getPoint(new WRAP.Geo.ScreenPoint(x, y));
      const coordinate = [currentPoint.lonDegree(), currentPoint.latDegree()];
      //DEPが未入力
      if (!depArrSelected.depSelected) {
        const text = FPEUtils.translation("click to determine DEP");
        if (this.depArrFeatures.length <= 1) {
          this._clearDepArrFeature(false);
          //DEP用feature
          this.depArrFeatures.push(this._makeDepArrFeature(coordinate));
          this.depArrLabelFeatures.push(this._makeDepArrLabelFeature(coordinate, text));

          //ARRが設定されている場合、ARR用featureとGeoLineをpush
          if (depArrSelected.arrSelected) {
            this.depArrFeatures.push(this._makeDepArrFeature(depArrSelected.arr.coordinate));
            this.depArrFeatures.push(this._makeDepArrGeoLineFeature(coordinate, depArrSelected.arr.coordinate));
            this.depArrLabelFeatures.push(this._makeDepArrLabelFeature(depArrSelected.arr.coordinate, depArrSelected.arr.name));
          }

          this.depArrFeatures.forEach(f => this.layer.addFeature(f));
          this.depArrLabelFeatures.forEach(f => this.layer.addFeature(f));
        }
        //DEP用featureの位置を調整
        else {
          const style = this.depArrFeatures[0].style();
          style.point = coordinate;
          if (depArrSelected.arrSelected) {
            const geoLineStyle = this.depArrFeatures[2].style();
            geoLineStyle.path = [coordinate, depArrSelected.arr.coordinate];
          }

          const labelStyle = this.depArrLabelFeatures[0].style();
          labelStyle.point = coordinate;
          labelStyle.text = text;
        }
      }
      //ARRが未入力
      else {
        const text = FPEUtils.translation("click to determine ARR");
        if (this.depArrFeatures.length <= 1) {
          this._clearDepArrFeature(false);
          //DEP用feature
          this.depArrFeatures.push(this._makeDepArrFeature(depArrSelected.dep.coordinate));
          this.depArrLabelFeatures.push(this._makeDepArrLabelFeature(depArrSelected.dep.coordinate, depArrSelected.dep.name));
          //ARR用feature
          this.depArrFeatures.push(this._makeDepArrFeature(coordinate));
          this.depArrLabelFeatures.push(this._makeDepArrLabelFeature(coordinate, text));

          //GeoLine
          this.depArrFeatures.push(this._makeDepArrGeoLineFeature(depArrSelected.dep.coordinate, coordinate));

          this.depArrFeatures.forEach(f => this.layer.addFeature(f));
          this.depArrLabelFeatures.forEach(f => this.layer.addFeature(f));
        }
        //ARR用featureの位置を調整
        else {
          const style = this.depArrFeatures[1].style();
          style.point = coordinate;

          const geoLineStyle = this.depArrFeatures[2].style();
          geoLineStyle.path = [depArrSelected.dep.coordinate, coordinate];

          const labelStyle = this.depArrLabelFeatures[1].style();
          labelStyle.point = coordinate;
          labelStyle.text = text;
          // WebGLの再描画条件に_modifiedがtrueであることが含まれる。addFeatureをしない場合、これが立たないのでここで立てる
          this.layer._modified = true;
        }
      }
      // WebGLの再描画条件
      this.layer._immediate = true;
      WRAP.Geo.invalidate();
    } else {
      this._clearDepArrFeature();
    }
  }

  _clearDepArrFeature(immediate=true) {
    let removed = false;
    let f;
    while( (f = this.depArrFeatures.shift()) !== undefined ) {
      this.layer.removeFeature(f);
      removed = true;
    }
    while( (f = this.depArrLabelFeatures.shift()) !== undefined ) {
      this.layer.removeFeature(f);
      removed = true;
    }
    if (removed && immediate) {
      // WebGLの再描画条件
      this.layer._immediate = true;
      WRAP.Geo.invalidate();
    }
  }

  _getDistance(p1, p2) {
    let lon1, lat1, lon2, lat2;
    if (Array.isArray(p1.coordinate) && p1.coordinate.length === 2) {
      lon1 = p1.coordinate[0];
      lat1 = p1.coordinate[1];
    }
    if (p1.lon !== undefined && p1.lon !== null && p1.lat !== undefined && p1.lat !== null) {
      lon1 = p1.lon;
      lat1 = p1.lat;
    }
    if (Array.isArray(p2.coordinate) && p2.coordinate.length === 2) {
      lon2 = p2.coordinate[0];
      lat2 = p2.coordinate[1];
    }
    if (p2.lon !== undefined && p2.lon !== null && p2.lat !== undefined && p2.lat !== null) {
      lon2 = p2.lon;
      lat2 = p2.lat;
    }
    // console.log(`p1=[${p1.type}]${p1.name}, ${lon1}, ${lat1}`);
    // console.log(`p2=[${p2.type}]${p2.name}, ${lon2}, ${lat2}`);
    return WRAP.Geo.getDistance(
      FPEUtils.convertWRAPPoint(lat1, lon1),
      FPEUtils.convertWRAPPoint(lat2, lon2)
    );
  }

  dataUpdate(flag) {
    if (flag) {
      this.dh.setAutoUpdate('data', this.autoUpdate);
    }
  }

  setPlanDataRevisionDispatcher(dispatcher) {
    this.setPlanDataRevision = dispatcher;
  }
}

export default FlightPlanList;

const HideButton = ({target, onClick}) => {
  const [tooltipVisible, setTooltipVisible] = useState(false);

  const handleClick = useCallback((e) => {
    if (onClick) {
      onClick(e, target);
    }
    setTooltipVisible(false);
  }, [onClick, target]);

  return (
    <IconButton aria-label="hide plan" size="small" onClick={handleClick}>
      <Tooltip title="hide plan" arrow PopperProps={{style: {zIndex: 10000}}} open={tooltipVisible}>
        <VisibilityIcon fontSize="small" color="action"
          onMouseEnter={()=>{setTooltipVisible(true)}}
          onMouseLeave={()=>{setTooltipVisible(false)}}
        />
      </Tooltip>
    </IconButton>
  );
};
